import React from "react";
import styled from "styled-components";
import tools from "../../../../../../helpers/tools";
import EmailLink from "../../../../../common/CommentList/common/EmailLink/EmailLink";
import PhoneLink from "../../../../../common/CommentList/common/PhoneLink/PhoneLink";
import SaveBtn from "../../../../../common/SaveBtn";
import useClientItem from "../useClientItem";
import useContextualTimeline from "../useContextualTimeline/useContextualTimeline";
import useUserInfos from "./useUserInfos";
import { estEligibleInvitation, libelleBoutonInvitation } from "./invitation";

// Pastille de statut : couleur sémantique (ok / ko) + libellé court visible,
// détail (et date) accessible au survol via le tooltip natif.
const StatusPill = ({ ok, icon, label, tooltip }) => (
  <span
    className={`user-pill ${ok ? "user-pill--ok" : "user-pill--ko"}`}
    title={tooltip || label}
  >
    <i className={`fa ${icon}`} />
    {label}
  </span>
);

const UserStatus = () => {
  const { client } = useClientItem();
  return (
    <StatusPill
      ok={client.isActivated}
      icon={client.isActivated ? "fa-check" : "fa-times"}
      label={client.isActivated ? "Compte activé" : "Compte non activé"}
    />
  );
};

const ActivationStatus = () => {
  const { client } = useClientItem();
  const tokenAt = client.activationTokenSendedAt;
  return (
    <StatusPill
      ok={!!tokenAt}
      icon="fa-envelope"
      label={tokenAt ? "Activation envoyée" : "Activation non envoyée"}
      tooltip={
        tokenAt
          ? `Activation envoyée le ${tools.formatDate(
              tokenAt,
              "dd.MM.yy HH:mm",
            )}`
          : "Activation non envoyée"
      }
    />
  );
};

const AvisGoogleStatus = () => {
  const { client } = useClientItem();
  const emailSentAt = client.emailAvisGoogleSentAt;
  const sentByNom = client.emailAvisGoogleSentByNom;
  return (
    <StatusPill
      ok={!!emailSentAt}
      icon="fa-star"
      label={emailSentAt ? "Avis Google envoyé" : "Avis Google non envoyé"}
      tooltip={
        emailSentAt
          ? `Email envoyé le ${tools.formatDate(emailSentAt, "dd.MM.yy HH:mm")}${
              sentByNom ? ` par ${sentByNom}` : ""
            }`
          : "Email avis Google non envoyé"
      }
    />
  );
};

// PER-2398 — affichée seulement pour les comptes éligibles à l'invitation ou déjà invités.
const InvitationStatus = () => {
  const { client } = useClientItem();
  const sentAt = client.invitationSentAt;
  const sentByNom = client.invitationSentByNom;
  return (
    <StatusPill
      ok={!!sentAt}
      icon="fa-paper-plane"
      label={sentAt ? "Invitation envoyée" : "Invitation non envoyée"}
      tooltip={
        sentAt
          ? `Invitation envoyée le ${tools.formatDate(sentAt, "dd.MM.yy HH:mm")}${
              sentByNom ? ` par ${sentByNom}` : ""
            }`
          : "Invitation à créer son mot de passe non envoyée"
      }
    />
  );
};

const UserInfos = () => {
  const {
    client,
    isSendingActivation,
    isSendingAvisGoogle,
    isSendingInvitation,
    isActivating,
    isUnlocking,
    isBlockUser,
    isBlocking,
    actions,
  } = useUserInfos();
  const { openTimeline } = useContextualTimeline();

  const actionBtnCN =
    "btn-default dropdown-item border font-weight-normal mx-0 mt-0 rounded-0 border-0 btn-sm w-100 d-center";

  // Initiales depuis prénom + nom uniquement (sans la civilité de getFullName).
  const initials =
    [client.firstname, client.lastname]
      .filter(Boolean)
      .map((n) => n.trim()[0])
      .join("")
      .toUpperCase() || "?";

  return (
    <UserCard>
      {/* En-tête : avatar initiales + nom */}
      <div className="user-card__header">
        <div className="user-card__avatar">{initials || "?"}</div>
        <div className="user-card__identity">
          {(isBlockUser || client.isLocked) && (
            <span className="badge badge-danger mb-1">Compte bloqué</span>
          )}
          <div className="user-card__name">{tools.getFullName(client)}</div>
        </div>
      </div>

      {/* Contacts */}
      <div className="user-card__contacts">
        <PhoneLink phone={client.phone} />
        <EmailLink email={client.email} />
      </div>

      {/* Statuts */}
      <div className="user-card__statuses">
        <UserStatus />
        <ActivationStatus />
        <AvisGoogleStatus />
        {(estEligibleInvitation(client) || client.invitationSentAt) && (
          <InvitationStatus />
        )}
      </div>

      {/* Métadonnée + actions */}
      <div className="user-card__footer">
        <div className="user-card__created">
          créé le {tools.formatDate(client.createdAt, "dd.MM.yy")}
        </div>

        <div className="dropdown">
          <button
            className="btn btn-default dropdown-toggle position-relative border btn-sm"
            type="button"
            id="dropdownMenuButton"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Actions du compte
            <i className="fa fa-caret-down ml-2" />
          </button>
          <div className="dropdown-menu py-0" aria-labelledby="dropdownMenuButton">
            <button
              data-priv="bo_updatecustomer"
              className={`${actionBtnCN} btn btn-light`}
              onClick={actions.openUserForm}
            >
              Modifier les informations
            </button>
            {estEligibleInvitation(client) ? (
              <SaveBtn
                dataPriv="bo_sendactivationmail"
                className={actionBtnCN}
                type="light"
                save={actions.sendInvitation}
                isSaving={isSendingInvitation}
                text={libelleBoutonInvitation(client)}
              />
            ) : (
              <SaveBtn
                dataPriv="bo_sendactivationmail"
                className={actionBtnCN}
                type="light"
                save={actions.sendActivation}
                isSaving={isSendingActivation}
                text="Renvoyer l'email d'activation"
              />
            )}
            <SaveBtn
              dataPriv="bo_sendactivationmail"
              className={actionBtnCN}
              type="light"
              save={actions.activateMail}
              isSaving={isActivating}
              text="Forcer l'activation"
            />
            <SaveBtn
              dataPriv="bo_envoyermailavisgoogle"
              className={actionBtnCN}
              type="light"
              save={actions.sendAvisGoogle}
              isSaving={isSendingAvisGoogle}
              text="Envoyer l'email Avis Google"
            />
            <SaveBtn
              dataPriv="bo_resetpasswordtries"
              className={actionBtnCN}
              type="light"
              save={actions.unlockAccount}
              isSaving={isUnlocking}
              text="Déverrouiller l'accès au compte"
            />
            <button
              className="btn btn-light border-0  w-100 font-weight-normal btn-sm"
              onClick={() =>
                openTimeline({
                  model: "users",
                  id: client.id,
                  searchString: "",
                })
              }
            >
              Événements du compte
            </button>
            {isBlockUser ? (
              <SaveBtn
                dataPriv="bo_lockaccount"
                className={actionBtnCN}
                type="default"
                save={actions.blockUserAccount}
                isSaving={isBlocking}
                text="Débloquer le compte"
              />
            ) : (
              <SaveBtn
                dataPriv="bo_lockaccount"
                className={`${actionBtnCN} text-white`}
                type="danger"
                save={actions.blockUserAccount}
                isSaving={isBlocking}
                text="Bloquer le compte"
              />
            )}
          </div>
        </div>
      </div>
    </UserCard>
  );
};

const UserCard = styled.div`
  background: #ffffff;
  border: 1px solid var(--ds-slate-200);
  border-radius: 8px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08),
    0 4px 12px rgba(15, 23, 42, 0.05);

  & .user-card__header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  & .user-card__avatar {
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--ds-blue-soft);
    color: var(--ds-blue);
    font-weight: 700;
    font-size: 15px;
    letter-spacing: 0.02em;
  }

  & .user-card__name {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--ds-slate-900);
  }

  & .user-card__contacts {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding-bottom: 10px;
    margin-bottom: 10px;
    border-bottom: 1px solid var(--ds-slate-200);
  }

  & .user-card__statuses {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }

  & .user-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
    line-height: 1.4;
    cursor: default;

    & i {
      font-size: 10px;
    }
  }

  & .user-pill--ok {
    background: var(--ds-success-soft);
    color: var(--ds-success);
  }

  /* État manquant = avertissement (ambre), pas erreur (rouge) :
     un compte non encore activé n'est pas une faute, juste une action en attente. */
  & .user-pill--ko {
    background: var(--ds-warning-soft);
    color: var(--ds-warning);
  }

  & .user-card__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  & .user-card__created {
    font-size: 12px;
    color: var(--ds-slate-500);
  }

  & .dropdown-toggle {
    align-self: flex-start;
  }
`;

export default UserInfos;
